import React, { createContext, useState } from "react";
import { ParameterListDialogProps } from "../components/Dialog/ParameterListDialog/ParameterListDialog";
import Parameter from "../api/models/test-series/Parameter";
import ParameterValue from "../api/models/test-series/ParameterValue";
import SubstrateSample from "../api/models/substrate/SubstrateSample";
import { SubstrateClassSelection } from "../api/models/substrate/SubstrateClassSelection";
import JSONHelper from "../helper/JSONHelper";
import TestRequestResponse from "../api/models/test-request/TestRequestResponse";

type SubstrateContext = "CUSTOMER_PART" | "CUSTOMER_STANDARD" | "TESA_STANDARD" | "NON_STANDARD";

interface SubstrateSampleContextState {
  testRequest: TestRequestResponse | null;
  substrateClasses: SubstrateClassSelection[];
  substrateContext: SubstrateContext[];
  substrateId: number | null;
  substrateObject: SubstrateSample | null;
  updateTestRequest: (value: TestRequestResponse | null) => void;
  updateSubstrateClasses: (updateFunction: (prev: SubstrateClassSelection[]) => SubstrateClassSelection[]) => void;
  updateSubstrateContext: (value: SubstrateContext[]) => void;
  updateSubstrateId: (value: number | null) => void;
  updateSubstrateObject: (value: any) => void;
  parameterListDialog: ParameterListDialogProps;
  showParameterListDialog: (
    title: string,
    parameters: Parameter[],
    parameterValues: ParameterValue[],
  ) => Promise<ParameterValue[]>;
}

const HIDE_PARAMETER_LIST_DIALOG: ParameterListDialogProps = {
  onSave: (): void => {},
  onClose: (): void => {},
  title: "",
  open: false,
  parameters: [],
  parameterValues: [],
  hasMinAndMaxValue: false,
};

export const SubstrateSampleContext = createContext<SubstrateSampleContextState>({
  testRequest: null,
  substrateClasses: [],
  substrateContext: [],
  substrateId: null,
  substrateObject: null,
  updateTestRequest: () => {},
  updateSubstrateClasses: () => {},
  updateSubstrateContext: () => {},
  updateSubstrateId: () => {},
  updateSubstrateObject: () => {},
  parameterListDialog: HIDE_PARAMETER_LIST_DIALOG,
  showParameterListDialog: () => Promise.reject(),
});

const LAST_USED_SUBSTRATE_CONTEXT_KEY = "lastUsedSubstrateContext";
const LAST_USED_SUBSTRATE_CLASS_SELECTION = "lastUsedSubstrateClasses";

const SubstrateSampleProvider: React.FunctionComponent = (props: any) => {
  const [testRequest, updateTestRequest] = useState<TestRequestResponse | null>(null);
  const [substrateClasses, setSubstrateClasses] = useState<SubstrateClassSelection[]>(
    JSONHelper.parseFromLocalStorage<SubstrateClassSelection[]>(LAST_USED_SUBSTRATE_CLASS_SELECTION) || [],
  );
  const [substrateContext, setSubstrateContext] = useState<SubstrateContext[]>(
    JSONHelper.parseFromLocalStorage<SubstrateContext[]>(LAST_USED_SUBSTRATE_CONTEXT_KEY) || [],
  );
  const [substrateId, setSubstrateId] = useState<number | null>(null);
  const [substrateObject, setSubstrateObject] = useState<SubstrateSample | null>(null);
  const [parameterListDialog, setParameterListDialog] = useState(HIDE_PARAMETER_LIST_DIALOG);

  const updateSubstrateClasses = (
    updateFunction: (prev: SubstrateClassSelection[]) => SubstrateClassSelection[],
  ): void => {
    setSubstrateClasses((prev) => {
      const newValue = updateFunction(prev);
      if (newValue !== prev) {
        localStorage.setItem(LAST_USED_SUBSTRATE_CLASS_SELECTION, JSON.stringify(newValue));
        return newValue;
      }
      return prev;
    });
  };

  const updateSubstrateContext = (newSubstrateContext: SubstrateContext[]): void => {
    setSubstrateContext((prev) => {
      if (prev.join() === newSubstrateContext.join()) {
        return prev;
      }
      localStorage.setItem(LAST_USED_SUBSTRATE_CONTEXT_KEY, JSON.stringify(newSubstrateContext));
      return newSubstrateContext;
    });
  };
  const updateSubstrateId = (newSubstrateId: number | null): void => {
    setSubstrateId(newSubstrateId);
  };
  const updateSubstrateObject = (newSubstrateObject: SubstrateSample): void => {
    setSubstrateObject(newSubstrateObject);
  };

  const showParameterListDialog = (
    title: string,
    parameters: Parameter[],
    parameterValues: ParameterValue[],
  ): Promise<ParameterValue[]> => {
    if (parameterListDialog.open) {
      parameterListDialog.onClose();
    }
    return new Promise((resolve, reject) => {
      setParameterListDialog({
        open: true,
        title,
        parameters,
        parameterValues,
        onClose: () => {
          reject();
          setParameterListDialog(HIDE_PARAMETER_LIST_DIALOG);
        },
        onSave: (values) => {
          resolve(values);
          setParameterListDialog(HIDE_PARAMETER_LIST_DIALOG);
        },
        hasMinAndMaxValue: true,
      });
    });
  };

  const { children } = props;

  return (
    <SubstrateSampleContext.Provider
      value={{
        testRequest,
        substrateClasses,
        substrateContext,
        substrateId,
        substrateObject,
        updateTestRequest,
        updateSubstrateClasses,
        updateSubstrateContext,
        updateSubstrateId,
        updateSubstrateObject,
        parameterListDialog,
        showParameterListDialog,
      }}
    >
      {children}
    </SubstrateSampleContext.Provider>
  );
};

export default SubstrateSampleProvider;
